import type React from "react"
import { useMediaQuery } from 'react-responsive'
import { useEffect, useMemo, useState, useId } from "react"

interface RoundedPolygonProps {
  width: number
  height: number 
  cornerRadius: number
  mobileWidth?: number
  mobileHeight?: number
  mobileCornerRadius?: number
  className?: string
  children?: React.ReactNode
  style?: React.CSSProperties
  imageUrl?: string
  videoUrl?: string
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  controls?: boolean
}

function RoundedPolygon({
  width,
  height,
  cornerRadius,
  mobileWidth,
  mobileHeight,
  mobileCornerRadius,
  className = "",
  children,
  style = {},
  imageUrl,
  videoUrl,
  autoPlay = true,
  loop = true,
  muted = true,
  controls = false,
}: RoundedPolygonProps) {
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const effectiveWidth = isMobile && mobileWidth !== undefined ? mobileWidth : width
  const effectiveHeight = isMobile && mobileHeight !== undefined ? mobileHeight : height
  const effectiveCornerRadius = isMobile && mobileCornerRadius !== undefined ? mobileCornerRadius : cornerRadius

  const points = useMemo(() => [
    [25, 0],
    [75, 0],
    [100, 25],
    [100, 100],
    [40, 100],
    [5, 40],
  ], [])

  const svgPath = useMemo(() => {
    if (points.length < 3) return ""
    let path = ""

    for (let i = 0; i < points.length; i++) {
      const current = points[i]
      const next = points[(i + 1) % points.length]
      const prev = points[(i - 1 + points.length) % points.length]

      const toPrev = [prev[0] - current[0], prev[1] - current[1]]
      const toNext = [next[0] - current[0], next[1] - current[1]]

      const toPrevLength = Math.hypot(...toPrev)
      const toNextLength = Math.hypot(...toNext)

      const toPrevNorm = [toPrev[0] / toPrevLength, toPrev[1] / toPrevLength]
      const toNextNorm = [toNext[0] / toNextLength, toNext[1] / toNextLength]

      const radiusPercent = (effectiveCornerRadius / effectiveWidth) * 100
      const radius = Math.min(radiusPercent, toPrevLength / 2, toNextLength / 2)

      const cp1 = [current[0] + toPrevNorm[0] * radius, current[1] + toPrevNorm[1] * radius]
      const cp2 = [current[0] + toNextNorm[0] * radius, current[1] + toNextNorm[1] * radius]

      if (i === 0) {
        path += `M ${cp1[0]} ${cp1[1]} Q ${current[0]} ${current[1]} ${cp2[0]} ${cp2[1]}`
      } else {
        path += ` L ${cp1[0]} ${cp1[1]} Q ${current[0]} ${current[1]} ${cp2[0]} ${cp2[1]}`
      }
    }

    return path + " Z"
  }, [points, effectiveCornerRadius, effectiveWidth])

  const clipPathId = useId()

  const [isMediaLoaded, setIsMediaLoaded] = useState(false)

  useEffect(() => {
    if (!imageUrl && !videoUrl) return

    if (imageUrl) {
      const img = new Image()
      img.src = imageUrl
      img.onload = () => setIsMediaLoaded(true)
    }

  }, [imageUrl, videoUrl])

  return (
    <div className="relative" style={{ width: `${effectiveWidth}vw`, height: `${effectiveHeight}vw` }}>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
            <path d={svgPath} transform="scale(0.01)" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <div
        className={className}
        style={{
          ...style,
          width: '100%',
          height: '100%',
          clipPath: `url(#${clipPathId})`,
          overflow: 'hidden',
        }}
      >
        {videoUrl ? (
          <video
            src={videoUrl}
            className="w-full h-full object-cover"
            autoPlay={autoPlay}
            loop={loop}
            muted={muted}
            controls={controls}
            onLoadedData={() => setIsMediaLoaded(true)}
          />
        ) : imageUrl ? (
          isMediaLoaded ? (
            <img src={imageUrl} alt="content" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200 animate-pulse">
              Loading...
            </div>
          )
        ) : (
          children
        )}
      </div>
    </div>
  )
}

export default function ClipPathComponent() {
  return (
    <div className="flex items-center justify-center w-full h-full p-2">
      <RoundedPolygon
        width={40}
        height={30}
        cornerRadius={2}
        mobileWidth={80}
        mobileHeight={60}
        mobileCornerRadius={4}
        videoUrl="/Home/splash_video_Home.mp4"
        className="flex items-center justify-center text-white font-bold text-xl shadow-2xl"
      />
    </div>
  )
}